import { useRef, useState } from "react";
import { api } from "./api";

// Shared PIN -> City/State auto-fill for booking address entry (Sender + Concierge).
// On a completed 6-digit PIN it resolves City/State via the backend Google Geocoding endpoint.
// Any PIN change clears stale derived City/State. City/State are always read-only in the UI.
// Ref-based so the debounced write never clobbers concurrent line1/landmark edits.
export function usePincodeAutofill(addr, onChange) {
  const [status, setStatus] = useState("idle"); // idle | resolving | ok | error
  const [error, setError] = useState("");
  const timer = useRef(null);
  const seq = useRef(0);
  const addrRef = useRef(addr);
  addrRef.current = addr;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const onPincodeChange = (raw) => {
    const pin = String(raw).replace(/\D/g, "").slice(0, 6);
    // Always clear stale derived values the moment the PIN changes.
    onChangeRef.current({ ...addrRef.current, pincode: pin, city: "", state: "" });
    setError("");
    setStatus("idle");
    if (timer.current) clearTimeout(timer.current);
    if (pin.length !== 6) return;
    const mySeq = ++seq.current;
    setStatus("resolving");
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/booking/pincode/${pin}`);
        if (mySeq !== seq.current) return; // a newer PIN superseded this lookup
        onChangeRef.current({ ...addrRef.current, pincode: pin, city: data.city, state: data.state });
        setStatus("ok");
      } catch (e) {
        if (mySeq !== seq.current) return;
        onChangeRef.current({ ...addrRef.current, pincode: pin, city: "", state: "" });
        setError(e?.response?.data?.detail?.message
          || "We couldn't resolve City/State for this PIN. Please check and re-enter.");
        setStatus("error");
      }
    }, 500);
  };

  return { status, error, onPincodeChange };
}
